import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HistoryIcon from "@mui/icons-material/History";
import ApiCallService from "../../../Service/ApiCallService";

const emptyText = "-";

const getUserName = (user) => {
  if (!user) return emptyText;

  const name = [user.userFirstName, user.userLastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || user.mobileNumber || user._id || emptyText;
};

const toLabel = (key) =>
  String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return emptyText;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return emptyText;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTimeOnly = (value) => {
  if (!value) return emptyText;
  if (!hasSelectedTime(value)) return emptyText;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const looksLikeDate = (key, value) =>
  key?.toLowerCase().includes("date") &&
  typeof value === "string" &&
  !Number.isNaN(new Date(value).getTime());

const hasSelectedTime = (value) =>
  Boolean(
    value &&
      !(
        (typeof value === "string" && !value.includes("T")) ||
        (typeof value === "string" && /T00:00:00(?:\.000)?Z$/.test(value))
      )
  );

const formatValue = (value, key) => {
  if (value === null || value === undefined || value === "") return emptyText;

  if (looksLikeDate(key, value)) return formatDateTime(value);

  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value;
  if (typeof value === "string") return value;

  if (Array.isArray(value)) {
    return value.length
      ? value.map((item) => formatValue(item, key)).join(", ")
      : emptyText;
  }

  if (typeof value === "object") {
    return (
      value.name ||
      value.heading ||
      value.userFirstName ||
      value.mobileNumber ||
      value._id ||
      JSON.stringify(value)
    );
  }

  return String(value);
};

const getActionMeta = (action) => {
  if (action === "CREATED") {
    return {
      label: "Created",
      color: "success",
      icon: <EventAvailableIcon fontSize="small" />,
    };
  }

  if (action === "DELETED") {
    return {
      label: "Deleted",
      color: "error",
      icon: <DeleteIcon fontSize="small" />,
    };
  }

  return {
    label: "Updated",
    color: "primary",
    icon: <EditIcon fontSize="small" />,
  };
};

const ChangeValue = ({ title, value, tone }) => (
  <Box
    sx={{
      p: 1.25,
      borderRadius: 1.5,
      bgcolor: tone === "old" ? "#fff5f5" : "#f0fff4",
      border: "1px solid",
      borderColor: tone === "old" ? "#ffd6d6" : "#c7f2d4",
      minHeight: 62,
    }}
  >
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        mt: 0.5,
        fontWeight: 700,
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const EventHistoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchEventHistory = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/calendarbooking/${id}?includeDeleted=true`,
        );
console.log("API response for event history:", response);
        setEventData(response?.data || null);
      } catch (err) {
        setError("Failed to load event history");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchEventHistory();
  }, [id]);

 const sortedHistory = useMemo(() => {
  const history = eventData?.history || [];

  return history
    .filter((item) => item.action !== "CREATED")
    .sort(
      (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
    );
}, [eventData?.history]);


  const getDisplayValue = (value, field) => {
    if (["calendarTypeId", "calendarSubTypeId"].includes(field)) {
      return value?.name || formatValue(value, field);
    }

    if (
      eventData?.meetingType === "Offline" &&
      ["startDate", "endDate"].includes(field) &&
      looksLikeDate(field, value)
    ) {
      return formatDateOnly(value);
    }

    if (
      eventData?.meetingType === "Online" &&
      ["startDate", "endDate"].includes(field) &&
      looksLikeDate(field, value)
    ) {
      return formatTimeOnly(value);
    }

    return formatValue(value, field);
  };

  const renderChange = (field, change) => {
    const previous =
      change && typeof change === "object" && "previous" in change
        ? change.previous
        : undefined;
    const next =
      change && typeof change === "object" && "new" in change
        ? change.new
        : change;

    return (
      <Paper key={field} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
          {toLabel(field)}
        </Typography>

        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={6}>
            <ChangeValue
              title="Previous"
              tone="old"
              value={getDisplayValue(previous, field)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ChangeValue title="New" value={getDisplayValue(next, field)} />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderHistoryItem = (entry, index) => {
    const meta = getActionMeta(entry?.action);
    const changes = entry?.changes || {};

    return (
      <Paper
        key={`${entry?.updatedAt || index}-${entry?.action || "history"}`}
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderLeft: "5px solid",
          borderLeftColor:
            meta.color === "error"
              ? "error.main"
              : meta.color === "success"
                ? "success.main"
                : "primary.main",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={meta.icon}
              label={meta.label}
              color={meta.color}
              size="small"
              sx={{ fontWeight: 800 }}
            />
            <Typography variant="body2" color="text.secondary">
              by {getUserName(entry?.updatedBy)}
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" fontWeight={700}>
            {formatDateTime(entry?.updatedAt)}
          </Typography>
        </Stack>

        <Stack spacing={1.25} sx={{ mt: 2 }}>
          {Object.keys(changes).length ? (
            Object.entries(changes).map(([field, change]) =>
              renderChange(field, change),
            )
          ) : (
            <Typography color="text.secondary">No field changes recorded.</Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          <HistoryIcon color="primary" />
          <Typography variant="h4" fontWeight={900}>
            Event History
          </Typography>
        </Stack>

        {!(eventData?.isDeleted || eventData?.deletedAt) ? (
          <Button variant="outlined" onClick={() => navigate(`/events/${id}`)}>
            Event Details
          </Button>
        ) : (
          <Box />
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              mb: 3,
              bgcolor: "#ffffff",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography variant="h5" fontWeight={900}>
                  {eventData?.heading || "Calendar Event"}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {eventData?.subheading || "No description available"}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`${sortedHistory.length} history entries`}
                  color="primary"
                  variant="outlined"
                />
                {eventData?.isDeleted || eventData?.deletedAt ? (
                  <Chip
                    label={`Deleted ${formatDateTime(eventData?.deletedAt)}`}
                    color="error"
                  />
                ) : (
                  <Chip label="Active" color="success" />
                )}
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" fontWeight={900}>
                Change Log
              </Typography>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {!sortedHistory.length ? (
              <Alert severity="info">No history has been recorded for this event.</Alert>
            ) : (
              <Stack spacing={1.5}>{sortedHistory.map(renderHistoryItem)}</Stack>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default EventHistoryPage;
